import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./Booking.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  checkAvailability,
} from "../services/hotelbookingService";

import {
  getCoinBalance,
} from "../services/coinTransactionsService";
import { decodeToken } from "../services/userService";
import CurrencyService from "../services/currencyService";
import PaymentService from "../services/PaymentService";
import { 
  FaCalendarAlt, 
  FaUsers, 
  FaMapMarkerAlt, 
  FaCoins, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaClipboardList,
  FaCreditCard,
  FaMoneyBillWave,
  FaShieldAlt,
  FaInfoCircle,
  FaLock
} from "react-icons/fa";

function Booking({ hotel, listingId, basePrice: propBasePrice, discountAmount: propDiscountAmount, currency: propCurrency, isIntegrated = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAvailable, setIsAvailable] = useState(true);
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [pendingBooking, setPendingBooking] = useState(null);
  
  const hotelData = hotel || location.state?.hotel;
  const currentListingId = listingId || location.state?.hotelId || hotelData?.id;
  
  const jwtToken = localStorage.getItem("jwt_token");
  let storedUser = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw && raw !== "undefined" && raw !== "null") {
      storedUser = JSON.parse(raw);
    }
  } catch (e) {
    storedUser = null;
  }
  const decodedTokenData = jwtToken ? decodeToken(jwtToken) : null;
  const userId = storedUser?.id || storedUser?.userId || decodedTokenData?.id;

  const [showPaymentSelect, setShowPaymentSelect] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [basePrice, setBasePrice] = useState(propBasePrice || 0);
  const [discountAmount, setDiscountAmount] = useState(propDiscountAmount || 0);
  const [coinApplied, setCoinApplied] = useState(0);
  const [currency, setCurrency] = useState(propCurrency || CurrencyService.selectedCurrency);
  const [priceAfterDiscount, setPriceAfterDiscount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  const [formData, setFormData] = useState({
    booking_date: today,
    checking_date: location.state?.checkingDate || "",
    checkout_date: location.state?.checkoutDate || "",
    number_of_guests: 1,
    pickup_requested: false,
    special_requests: "",
    use_coin_balance: 0,
  });

  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const hotelPrice = hotelData?.price || location.state?.basePrice || location.state?.finalPrice || 0;

  const getNextDay = (dateStr) => {
    if (!dateStr) return today;
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    const onCurrencyChange = (e) => setCurrency(e.detail);
    window.addEventListener("currencyUpdate", onCurrencyChange);
    return () => window.removeEventListener("currencyUpdate", onCurrencyChange);
  }, []);

  const calculateStayAndPrice = useCallback(
    (checkIn, checkOut, useCoins, balance) => {
      if (checkIn && checkOut) {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        if (days > 0) {
          const rawBase = hotelPrice * days;
          setBasePrice(rawBase);
          const disc = propDiscountAmount || 0;
          setDiscountAmount(disc);
          const afterDisc = rawBase - disc;
          setPriceAfterDiscount(afterDisc);
          
          let coinsToApply = 0;
          if (useCoins && balance > 0) {
            const coinVal = Number(process.env.REACT_APP_COIN_VALUE || 1);
            coinsToApply = Math.min(balance * coinVal, afterDisc);
          }
          setCoinApplied(coinsToApply);
          setTotalPrice(afterDisc - coinsToApply);
          return;
        }
      }
      setBasePrice(0);
      setDiscountAmount(0);
      setCoinApplied(0);
      setTotalPrice(0);
    },
    [hotelPrice, propDiscountAmount]
  );

  useEffect(() => {
    if (!userId) return;
    const fetchBalance = async () => {
      try {
        const balance = await getCoinBalance(userId);
        setCoinBalance(balance);
        calculateStayAndPrice(formData.checking_date, formData.checkout_date, formData.use_coin_balance, balance);
      } catch (err) { console.error("Failed to load coin balance:", err); }
    };
    fetchBalance();
  }, [userId, calculateStayAndPrice, formData.checking_date, formData.checkout_date, formData.use_coin_balance]);

  const runAvailabilityCheck = useCallback(async (checkIn, checkOut) => {
    if (!checkIn || !checkOut || !currentListingId) return;
    try {
      const data = await checkAvailability(currentListingId, checkIn, checkOut);
      setIsAvailable(data.available);
      setAvailabilityMessage(data.message);
    } catch (error) {
      console.error("Availability check failed:", error);
      setIsAvailable(false);
      setAvailabilityMessage("Unable to check availability.");
    }
  }, [currentListingId]);

  useEffect(() => {
    if (formData.checking_date && formData.checkout_date && currentListingId) {
      runAvailabilityCheck(formData.checking_date, formData.checkout_date);
    }
  }, [formData.checking_date, formData.checkout_date, currentListingId, runAvailabilityCheck]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "pickup_requested") {
      const boolVal = value === "yes" ? true : value === "no" ? false : checked;
      setFormData(prev => ({ ...prev, pickup_requested: boolVal }));
      return;
    }
    if (name === "use_coin_balance") {
      const updatedData = { ...formData, use_coin_balance: checked ? 1 : 0 };
      setFormData(updatedData);
      calculateStayAndPrice(updatedData.checking_date, updatedData.checkout_date, updatedData.use_coin_balance, coinBalance);
      return;
    }
    const updatedFormData = { ...formData, [name]: type === "checkbox" ? checked : value };
    setFormData(updatedFormData);
    if (name === "checking_date" || name === "checkout_date") {
      const checkInDate = name === "checking_date" ? value : updatedFormData.checking_date;
      const checkOutDate = name === "checkout_date" ? value : updatedFormData.checkout_date;
      calculateStayAndPrice(checkInDate, checkOutDate, updatedFormData.use_coin_balance, coinBalance);
      runAvailabilityCheck(checkInDate, checkOutDate);
    }
  };


  const handlePaymentsLkPayment = async (bookingData, method) => {
    try {
      const isAdvance = Boolean(method?.isAdvance);
      const result = await PaymentService.createPaymentsLkCheckout({
        bookingType: "hotel",
        bookingId: bookingData.id,
        clientType: "web",
        isAdvance,
      });

      if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        alert(result?.error || "Failed to initiate Payments.lk secure checkout.");
      }
    } catch (err) {
      console.error("Payments.lk checkout initiation error:", err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      alert("Failed to initiate payment: " + errMsg);
    }
  };

  const handleKokoPayment = async (booking) => {
    if (!booking) return;
    try {
      const payload = {
          bookingId: booking.id, // Pass existing booking ID to avoid duplicate creation
          userId: booking.user_id || userId,
          listingId: booking.listing_id || currentListingId,
          price: totalPrice || booking.price,
          checkingDate: booking.checking_date,
          checkoutDate: booking.checkout_date,
          numberOfGuests: booking.number_of_guests,
          pickupRequested: booking.pickup_requested,
          specialRequests: booking.special_requests,
          isAdvance: false,
          coins_used_amount: coinApplied || 0,
      };

      const response = await axios.post(`${BACKEND_URL}/payment/koko/start`, payload);
      const { actionUrl, fields } = response.data || {};

      if (!actionUrl || !fields || Object.keys(fields).length === 0) {
        alert("Koko payment service is temporarily unavailable. Please try another payment method or contact support.");
        return;
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = actionUrl;

      for (const key in fields) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = fields[key];
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("KOKO Error:", err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      alert("Failed to start Koko payment: " + errMsg);
    }
  };

  const handleConfirmBooking = async (e) => {
    e.preventDefault();

    if (!userId) {
      alert("Please log in or sign up to complete your reservation.");
      navigate("/login");
      return;
    }

    if (!currentListingId) {
      alert("Hotel listing reference is missing. Please select a valid hotel.");
      return;
    }

    if (!isAvailable) { 
      alert(availabilityMessage || "Dates not available."); 
      return; 
    }
    if (!formData.checking_date || !formData.checkout_date) { 
      alert("Please select dates."); 
      return; 
    }
    
    try {
      const bookingPayload = { 
        booking_date: formData.booking_date, 
        checking_date: formData.checking_date, 
        checkout_date: formData.checkout_date, 
        listing_id: currentListingId, 
        user_id: userId, 
        price: totalPrice, 
        price_after_discount: priceAfterDiscount, 
        coins_used_amount: coinApplied, 
        number_of_guests: formData.number_of_guests, 
        pickup_requested: formData.pickup_requested, 
        special_requests: formData.special_requests, 
        use_coin_balance: formData.use_coin_balance ? 1 : 0,
        payment_status_id: 2, 
        booking_status_id: 1,
        payment_method_id: 1
      };

      const response = await axios.post(`${BACKEND_URL}/bookings`, bookingPayload, {
        headers: jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}
      });
      
      if (response.data && (response.data.booking || response.data.id)) {
        setPendingBooking(response.data.booking || response.data);
        setShowPaymentSelect(true);
      } else {
        alert("Failed to create booking.");
      }
    } catch (err) {
      console.error("Booking creation error:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      alert("Error creating booking: " + errorMsg);
    }
  };

  if (!hotelData) return <div className="booking-main-container"><p>No hotel data found.</p></div>;

  if (showPaymentSelect && pendingBooking) {
    return (
      <div className={`booking-main-container ${isIntegrated ? 'integrated' : ''}`}>
        <h1 className="booking-title1">{isIntegrated ? 'Pay & Secure' : 'Select Payment Method'}</h1>
        {!isIntegrated && (
          <p style={{ textAlign: 'center', color: '#718096', marginBottom: '40px' }}>
            Your booking #{pendingBooking.id} is ready. Choose how you'd like to pay.
          </p>
        )}

        <div className="booking-section animate-fade-in" style={{ margin: '0 auto' }}>
          <div className="payment-grid">
            <div className="payment-option" onClick={() => handlePaymentsLkPayment(pendingBooking, { isAdvance: false })}>
              <div className="option-icon"><FaCreditCard /></div>
              <div className="option-info">
                <span className="option-title">Full Payment</span>
                <span className="option-desc">Secure payment via Payments.lk Hosted Gateway</span>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#2b6cb0' }}>
                  {CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(totalPrice, { fromCurrency: "LKR" }).toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: '#a0aec0' }}>Total Amount</div>
              </div>
            </div>

            <div className="payment-option" onClick={() => handlePaymentsLkPayment(pendingBooking, { isAdvance: true })}>
              <div className="option-icon"><FaMoneyBillWave /></div>
              <div className="option-info">
                <span className="option-title">Advance Payment (50%)</span>
                <span className="option-desc">Pay half now via Payments.lk, balance at check-in</span>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#38a169' }}>
                  {CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(totalPrice / 2, { fromCurrency: "LKR" }).toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: '#a0aec0' }}>Pay Now</div>
              </div>
            </div>

            <div className="payment-option koko" onClick={() => handleKokoPayment(pendingBooking)}>
              <div className="option-icon" style={{ background: '#000', color: '#fff', fontSize: '18px', fontWeight: '900' }}>K.</div>
              <div className="option-info">
                <span className="option-title">Koko</span>
                <span className="option-desc">3 Interest-free installments</span>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#000' }}>
                  {CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(totalPrice / 3, { fromCurrency: "LKR" }).toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: '#a0aec0' }}>Per Month</div>
              </div>
            </div>
          </div>

          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '14px 18px',
            marginTop: '20px',
            fontSize: '12.5px',
            color: '#4a5568',
            lineHeight: '1.6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#2d3748', marginBottom: '6px' }}>
              <FaInfoCircle color="#3182ce" /> Payment &amp; Cancellation Governance (v2026.09.01 | Draft: v2.0.0)
            </div>
            <p style={{ margin: '0 0 6px 0' }}>
              <strong>Merchant:</strong> SOLOWAY.LK (PVT) LTD (Company Reg: PV 00373363) (Booking Ref: <code>BOOK_{pendingBooking.id}</code>).
            </p>
            <p style={{ margin: '0 0 6px 0' }}>
              Card checkouts are processed externally via <strong>Payments.lk</strong> (brand operated by Payable (Pvt) Ltd). SoloWay does not store full card numbers or CVVs.
            </p>
            <p style={{ margin: '0 0 6px 0' }}>
              <FaLock style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              <strong>Confirmation Notice:</strong> Your booking remains in pending verification until an authoritative webhook or server-to-server confirmation is received. Returning from checkout does not independently prove payment.
            </p>
            <p style={{ margin: 0, fontSize: '12px' }}>
              Standard Cancellation: &gt;48h (100% refund eligibility), 24–48h (50%), &lt;24h (non-refundable). Settlement timing depends on issuing bank clearing. See <a href="/policies?tab=refunds" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'underline' }}>Full Platform Policies</a>.
            </p>
          </div>

          <div className="security-badge" style={{ marginTop: '20px', justifyContent: 'center', color: '#a0aec0', fontSize: '14px' }}>
            <FaShieldAlt style={{ marginRight: '8px' }} /> 256-bit Secure SSL Encryption
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`booking-main-container ${isIntegrated ? 'integrated' : ''}`}>
      {!isIntegrated && <h2 className="booking-title1">Complete Your Booking</h2>}

      <form onSubmit={handleConfirmBooking} className="booking-form">
        <div className="booking-section">
          <h3 className="booking-subtitle"><FaCalendarAlt /> Stay Details</h3>
          <div className="booking-form-row">
            <label>Check-in Date</label>
            <input type="date" name="checking_date" value={formData.checking_date} min={today} onChange={handleChange} required />
          </div>
          <div className="booking-form-row">
            <label>Check-out Date</label>
            <input type="date" name="checkout_date" value={formData.checkout_date} min={getNextDay(formData.checking_date)} onChange={handleChange} required />
          </div>

          {availabilityMessage && (
            <div className={`availability-message ${isAvailable ? "available" : "unavailable"}`}>
              {isAvailable ? <FaCheckCircle /> : <FaExclamationTriangle />}
              {availabilityMessage}
            </div>
          )}

          <div className="booking-form-row" style={{ marginTop: '20px' }}>
            <label><FaUsers /> Number of Guests</label>
            <input type="number" name="number_of_guests" min="1" value={formData.number_of_guests} onChange={handleChange} />
          </div>
        </div>

        <div className="booking-section">
          <h3 className="booking-subtitle"><FaMapMarkerAlt /> Services</h3>
          <div className="booking-form-row">
            <label>Pickup Service</label>
            <div className="pickup-options">
              <label className="pickup-option">
                <input type="radio" name="pickup_requested" value="yes" checked={formData.pickup_requested === true} onChange={handleChange} />
                <span className="pickup-text">Yes</span>
              </label>
              <label className="pickup-option">
                <input type="radio" name="pickup_requested" value="no" checked={formData.pickup_requested === false} onChange={handleChange} />
                <span className="pickup-text">No</span>
              </label>
            </div>
          </div>

          <div className="booking-form-row" style={{ marginTop: '20px' }}>
            <label><FaCoins /> Rewards</label>
            <div className="toggle-wrapper">
              <label className="toggle-switch">
                <input type="checkbox" name="use_coin_balance" checked={!!formData.use_coin_balance} onChange={handleChange} />
                <span className="toggle-slider" />
              </label>
              <div style={{ marginLeft: 10, color: "#2b6cb0", fontWeight: 700, fontSize: '13px' }}>
                Use Coins (Rs {CurrencyService.convert(coinBalance * Number(process.env.REACT_APP_COIN_VALUE || 1), { fromCurrency: "LKR", toCurrency: currency }).toFixed(2)})
              </div>
            </div>
          </div>
        </div>

        <div className="booking-section">
          <h3 className="booking-subtitle"><FaCoins /> Price</h3>
          <div className="price-breakdown">
            <div className="price-row">
              <span>Base</span>
              <span>{CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(basePrice, { fromCurrency: "LKR" }).toFixed(2)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="price-row">
                <span>Discount</span>
                <span style={{ color: "#e53e3e" }}>-{CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(discountAmount, { fromCurrency: "LKR" }).toFixed(2)}</span>
              </div>
            )}
            {formData.use_coin_balance > 0 && (
              <div className="price-row">
                <span>Coins</span>
                <span style={{ color: "#38a169" }}>-{CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(coinApplied, { fromCurrency: "LKR" }).toFixed(2)}</span>
              </div>
            )}
            <hr style={{ border: 'none', borderTop: '1px solid #edf2f7', margin: '12px 0' }} />
            <div className="price-row total">
              <strong>Total</strong>
              <strong style={{ fontSize: isIntegrated ? '20px' : '24px', color: '#2b6cb0' }}>{CurrencyService.getCurrencySymbol(currency)} {CurrencyService.convert(totalPrice, { fromCurrency: "LKR" }).toFixed(2)}</strong>
            </div>
          </div>
        </div>

        {!isIntegrated && (
          <div className="booking-section">
            <h3 className="booking-subtitle"><FaClipboardList /> Special Requests</h3>
            <textarea name="special_requests" placeholder="Any special requests?" value={formData.special_requests} onChange={handleChange} rows="3" />
          </div>
        )}

        <div className="button-wrapper">
          <button type="submit" className="booking-confirm-btn" disabled={!isAvailable}>
            {isIntegrated ? 'Confirm Reservation' : 'Confirm & Pay'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Booking;
